import CloudOutlined from '@ant-design/icons/CloudOutlined';
import RobotOutlined from '@ant-design/icons/RobotOutlined';
import WarningFilled from '@ant-design/icons/WarningFilled';
import Popover from 'antd/lib/popover';
import Typography from 'antd/lib/typography';
import { formatDistance } from 'date-fns';
import { useMemo } from 'react';
import { ICompany } from '../../domain/company/interface';
import { ICloudVen, ICustomerVen, VEN_STATUS_COLOR_MAP } from '../../domain/ven/interface';
import { CircleIcon } from '../icons/icons';

const { Text } = Typography;

export interface IVenIconProps {
    ven: ICustomerVen | ICloudVen;
    controlProvider?: ICompany | null;
}

export function VenIcon({ ven, controlProvider }: IVenIconProps) {
    const isCompanyVen = 'company_id' in ven && ven.company_id !== null;

    const lastSeenString = ven.ven_date_online
        ? formatDistance(new Date(ven.ven_date_online), new Date(), { addSuffix: true, includeSeconds: true })
        : null;

    const { icon, title } = useMemo(() => {
        const color = VEN_STATUS_COLOR_MAP[ven.status];

        if (!isCompanyVen) {
            return {
                icon: <CloudOutlined style={{ fontSize: '28px', color }} />,
                title: 'Cloud VEN',
            };
        }

        if (ven.mac_address) {
            return {
                icon: <RobotOutlined style={{ fontSize: '26px', color }} />,
                title: 'Enersponse Hardware',
            };
        }

        // Attached to control provider sites
        if (ven.control_provider_id) {
            return {
                icon: <CircleIcon style={{ fontSize: '28px', color }} />,
                title: controlProvider?.company_name
                    ? `Customer VEN (Control Provider: ${controlProvider.company_name})`
                    : 'Customer VEN (Control Provider Sites)',
            };
        }

        // Attached to all sites
        if (isCompanyVen && ven.site_id === null) {
            return {
                icon: <CircleIcon style={{ fontSize: '28px', color }} />,
                title: 'Customer VEN (All Company Sites)',
            };
        }

        // Attached to single site
        return {
            icon: <CircleIcon style={{ fontSize: '28px', color }} />,
            title: 'Customer VEN',
        };
    }, [controlProvider?.company_name, isCompanyVen, ven]);

    const { errorIcon, errorText } = useMemo(() => {
        if (ven.ven_error) {
            return {
                errorText: ven.ven_error,
                errorIcon: (
                    <WarningFilled
                        style={{
                            fontSize: '16px',
                            color: '#faad14',
                            position: 'absolute',
                            left: '50%',
                            top: '50%',
                        }}
                    />
                ),
            };
        }

        return {
            errorIcon: null,
            errorText: null,
        };
    }, [ven.ven_error]);

    const statusContent = (
        <div style={{ maxWidth: '200px' }}>
            <div className="device-icon-popover-status">
                <Text type={ven.status === 'online' ? 'success' : 'danger'}>{ven.status?.toUpperCase()}</Text>
            </div>

            {errorText && <Text mark>{errorText}</Text>}

            {lastSeenString && <div className="device-icon-popover-lastseen">Last seen:&nbsp;{lastSeenString}</div>}
        </div>
    );

    const content = (
        <div className="device-icon-popover">
            <div className="device-icon-popover-title">{title}</div>
            {statusContent}
        </div>
    );

    return (
        <span style={{ display: 'inline-block', position: 'relative', width: 32, height: 32, verticalAlign: 'middle' }}>
            {title ? <Popover content={content}>{icon}</Popover> : icon}
            {errorIcon}
        </span>
    );
}
